import { useCallback } from 'react';
import { showServerErrorToast, showNotificationToast } from 'src/components/toast/toast-helper';
import {
  BaseScoreSection,
  BaseVariableSection,
  StudyNoteConfigCreationDto,
  StudyNoteConfigPatchingDto,
} from 'src/services/api/api-client';
import { ReactComponent as NewCommentIcon } from 'assets/img/notification/new_comment_20.svg';
import { SaveFormParams } from '../../builder/generalFormBuilder.t';
import { useQueryClient } from '@tanstack/react-query';
import { useCreateFormConfigModal } from './useCreateFormConfigModal';
import {
  createMonitoringFormsConfig,
  patchMonitoringFormsConfig,
  removeMonitoringFormsConfig,
  uploadConfigFile,
} from 'src/services/api/api-client/ConfigurableMonitoringFormsClient';
import {
  getMonitoringFormConfigQueryKey,
  getMonitoringFormConfigsQueryKey,
} from 'src/services/api/api-client/ConfigurableMonitoringFormsQuery';
import { useScopedTranslation } from 'src/application/localisation/useScopedTranslation';
import { useStudy } from 'src/helpers/hooks/useStudy';
import { getDoctorStudyByIdQueryKey } from 'src/services/api/api-client/StudyQuery';
import { useRevalidator } from 'react-router';

export const useMonitoringFormHandlers = (deps: {
  selectedConfigId?: number;
  setConfigId: (id?: number) => void;
  initialTitle: string;
}) => {
  const revalidator = useRevalidator();
  const { selectedConfigId, setConfigId: setSelectedConfig, initialTitle } = deps;

  const study = useStudy();
  const { t } = useScopedTranslation('GeneralFormBuilder.Toasts');
  const queryClient = useQueryClient();
  const createConfigModal = useCreateFormConfigModal();

  const deleteHandler = useCallback(async () => {
    try {
      if (!selectedConfigId) throw new Error('Config not selected');
      await removeMonitoringFormsConfig(selectedConfigId);
      setSelectedConfig(undefined);
      await queryClient.invalidateQueries(getDoctorStudyByIdQueryKey(study!.id));
      revalidator.revalidate();

      showNotificationToast(t('FormDeletedToast.Message'), t('FormDeletedToast.Header'), NewCommentIcon, 3000);
    } catch (error) {
      showServerErrorToast(error);
    }
  }, [queryClient, revalidator, selectedConfigId, setSelectedConfig, study, t]);

  const createFormConfigHandler = useCallback(
    async (params: SaveFormParams, newTitle: string) => {
      try {
        const { nodes, scoreSections, variables } = params;
        const created = await createMonitoringFormsConfig(
          new StudyNoteConfigCreationDto({
            layout: nodes,
            title: newTitle,
            scoreSections: scoreSections?.map((x) => new BaseScoreSection(x)) ?? null,
            variables: variables?.map((x) => new BaseVariableSection(x)) ?? null,
          }),
          study!.id,
        );

        await Promise.all([
          queryClient.invalidateQueries(getDoctorStudyByIdQueryKey(study!.id)),
          queryClient.invalidateQueries(getMonitoringFormConfigsQueryKey(study!.id)),
        ]);

        revalidator.revalidate();

        setSelectedConfig(created.id);
        showNotificationToast(t('FormSavedToast.Message'), t('FormSavedToast.Header'), NewCommentIcon, 3000);
      } catch (error) {
        showServerErrorToast(error);
        throw error;
      }
    },
    [study, queryClient, revalidator, setSelectedConfig, t],
  );

  const patchFormConfigHandler = useCallback(
    async (params: SaveFormParams) => {
      if (!selectedConfigId) throw new Error('selectedConfigId has not provided');
      try {
        const { nodes, scoreSections, variables } = params;
        await patchMonitoringFormsConfig(
          selectedConfigId,
          new StudyNoteConfigPatchingDto({
            layout: nodes,
            title: initialTitle,
            scoreSections: scoreSections?.map((x) => new BaseScoreSection(x)) ?? null,
            variables: variables?.map((x) => new BaseVariableSection(x)) ?? null,
          }),
        );

        await Promise.all([
          queryClient.invalidateQueries(getMonitoringFormConfigQueryKey(selectedConfigId)),
          queryClient.invalidateQueries(getDoctorStudyByIdQueryKey(study!.id)),
        ]);

        revalidator.revalidate();
        showNotificationToast(t('FormSavedToast.Message'), t('FormSavedToast.Header'), NewCommentIcon, 3000);
      } catch (error) {
        showServerErrorToast(error);
      }
    },
    [initialTitle, queryClient, revalidator, selectedConfigId, study, t],
  );

  const onSaveAs = useCallback(
    async (params: SaveFormParams) => {
      await createConfigModal.open({
        callback: (data) => createFormConfigHandler(params, data.title),
        initialValues: { title: initialTitle },
      });
    },
    [createConfigModal, createFormConfigHandler, initialTitle],
  );

  const onSave = useCallback(
    async (params: SaveFormParams) => {
      if (!selectedConfigId) await onSaveAs(params);
      else await patchFormConfigHandler(params);
    },
    [selectedConfigId, patchFormConfigHandler, onSaveAs],
  );

  const uploadFileFunction = useCallback(
    (localFile: any) =>
      uploadConfigFile(study!.id, {
        data: localFile.file,
        fileName: localFile.fileName,
      }),
    [study],
  );

  const builderProps = {
    onDelete: selectedConfigId ? deleteHandler : undefined,
    onSave: onSave,
    onSaveAs: selectedConfigId ? onSaveAs : undefined,
    uploadFileFunction,
  };

  return { builderProps, createConfigModal: createConfigModal.modal };
};
